<template>
  <div v-if="!activeVariant.productHasExpired" class="InstantBuyBox">
    <!-- notify when back -->
    <div v-if="activeVariant && !activeVariant.canAddToCart && !activeVariant.productHasExpired && !globalContent.getAllowBackorders">
      <button
        class="btn btn--secondary mobOnly:!h-32 mobOnly:!px-12"
        @click="showNotifyModal"
      >
        {{ $t('productPage.sizeSelector.watchStockBtn') }}
      </button>
    </div>

    <!-- available to buy -->
    <div v-else class="relative">
      <div class="flex items-center gap-6">
        <div
          v-if="userStore.isSalesRepUser && activeVariant.canAddToCart && !activeVariant.isComingSoonProduct"
          class="border border-light relative bg-lightest items-center
            justify-center text-center flex w-32 h-[34px] desk:w-40 desk:h-[42px] cursor-pointer group"
          :class="{
            'pointer-events-none opacity-70': cartStore.isLoading,
          }"
          @click="change(true)"
        >
          <img
            class="w-16 h-16 outline outline-8 outline-transparent transition-all
            group-hover:bg-brandLight group-hover:outline-brandLight"
            src="/icons/gift.svg"
            alt=""
          >
          <div
            v-if="giftedAmount > 0"
            class="flex items-center justify-center w-20 h-20 absolute -top-8 -right-[6px] desk:-top-4
            desk:-right-[5px] bg-brandPrimary type-headline-xs rounded-full"
          >
            {{ giftedAmount }}
          </div>
        </div>
        <TouchNumber
          v-if="!activeVariant.isComingSoonProduct"
          id=""
          ref="touchNumberRef"
          v-model="qty"
          :index="0"
          :show-buttons-on-desktop="true"
          :no-input-field="true"
          :attr="attr"
          :loading="cartStore.isLoading"
          @change="change(false)"
        />
      </div>
      <div ref="warningWrapperRef" class="absolute top-full right-0 z-10 whitespace-nowrap">
        <RecommendedQtyWarning
          :active-variant="activeVariant"
          :cart-qty="qty"
          :current-qty="qty"
          :can-buy-more-amount="canBuyMoreAmount"
          :arrow-offset="arrowOffset"
          :no-top-margin="true"
          :no-wrap="true"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ProductVariantModel } from '#models/productVariant';
import { ProductModel } from '#models/product';
import TouchNumber from '~/components/form-elements/TouchNumber.vue';
import RecommendedQtyWarning from '~/components/product/RecommendedQtyWarning.vue';
import { useUiStore } from '~/store/ui';
import { useUserStore } from '~/store/user';
import { useCartStore } from '~/store/cart';
import { useGlobalContentStore } from '~/store/globalContent';

const uiStore = useUiStore();
const userStore = useUserStore();
const cartStore = useCartStore();
const globalContent = useGlobalContentStore();
const qty = ref(0);
const touchNumberRef = ref<{ $el: HTMLElement } | null>(null);
const warningWrapperRef = ref<HTMLElement | null>(null);
const arrowOffset = ref(0);
const props = defineProps<{
  activeVariant: ProductVariantModel,
  productItem: ProductModel,
}>();

const canBuyMoreAmount = computed(() => {
  const cartQty = qty.value;
  const available = props.activeVariant.stockValue - cartQty;

  if (props.activeVariant.hasFixedRecommendedQty && props.activeVariant.recommendedQty && available > props.activeVariant.recommendedQty) {
    if (cartQty >= props.activeVariant.recommendedQty) {
      return 0;
    }
    return props.activeVariant.recommendedQty;
  }

  return available;
});

const attr = computed(()=> {
  const out = { min: 0 } as {min: number, max?:number};
  if (!props.activeVariant.isOnDemandProduct && !globalContent.getAllowBackorders) {
    if (props.activeVariant.hasFixedRecommendedQty && props.activeVariant.recommendedQty && props.activeVariant.stockValue > props.activeVariant.recommendedQty) {
      out.max = props.activeVariant.recommendedQty;
    } else {
      out.max = props.activeVariant.stockValue;
    }
  }
  return out;
});

onMounted(() => {
  getQtyFromCart();
  getGiftedAmountFromCart();
  if (touchNumberRef.value?.$el && warningWrapperRef.value) {
    const el = touchNumberRef.value.$el as HTMLElement;
    const containerWidth = warningWrapperRef.value.offsetParent
      ? (warningWrapperRef.value.offsetParent as HTMLElement).offsetWidth
      : 0;
    const centerFromLeft = el.offsetLeft + el.offsetWidth / 2;
    arrowOffset.value = containerWidth - centerFromLeft;
  }
});

// Listen to changes from minicart
watch(()=> cartStore.getQuantityFromPartNo(props.activeVariant.partNo, false, false, userStore.isSalesRepUser), () => {
  getQtyFromCart();
});

// Listen to changes from minicart
watch(()=> cartStore.getQuantityFromPartNo(props.activeVariant.partNo, false, userStore.isSalesRepUser), () => {
  getGiftedAmountFromCart();
});

const getQtyFromCart = () => {
  qty.value = cartStore.getQuantityFromPartNo(props.activeVariant.partNo, false, false, userStore.isSalesRepUser);
};

const giftedAmount = ref(0);
const getGiftedAmountFromCart = () => {
  giftedAmount.value = cartStore.getQuantityFromPartNo(props.activeVariant.partNo, false, userStore.isSalesRepUser);
};

const change = (addAsGift: boolean = false) => {
  if (props.activeVariant.isComingSoonProduct) {
    return false;
  }

  cartStore.changeDebouncedQueue({
    partNo: props.activeVariant.partNo,
    quantity: addAsGift ? giftedAmount.value + 1 : qty.value,
    product: props.activeVariant,
    addAsGift: addAsGift,
  });
};

const showNotifyModal = () => {
  uiStore.notifyModalPartNo = props.activeVariant.partNo;
  uiStore.setShowNotify(true);
};

</script>

<style scoped lang="postcss">
</style>
